import { Component, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { CookieService } from 'ngx-cookie-service';
import { HttpService } from 'src/app/services/http.service';
import { Constants } from 'src/app/utils/constants';
import { Region } from '../../bo/region';
import { Site } from '../../bo/site';
import { WorkflowEtapeInstance } from '../../bo/workflowEtapeInstance';
import { OrganizationDto } from '../../bo/organization';
import { TraductionService } from '../../services/traduction.service';
import { UtilisateurService } from '../../services/utilisateur.service';
import { ModaleChangerLangueComponent } from '../modale-changer-langue/modale-changer-langue.component';
import { ModaleChangerMotPasseComponent } from '../modale-changer-mot-passe/modale-changer-mot-passe.component';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { environment } from 'src/environments/environment';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { SharedOrganizationService } from 'src/app/services/organization.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { FeatureFlag } from 'src/app/bo/featureFlag';
import {
  AREA_FEATURE_NAME,
  getOrganizationIdsFromSites,
  isAreaEnabledForUser,
} from 'src/app/Guards/landingRedirectGuard';

interface RouteConfig {
  pattern: string;
  infoRoute: string;
  urlContextualisee?: boolean;
  isSetupRegion?: boolean;
  isUserDashboard?: boolean;
}

interface RouteInfo {
  infoRoute: string;
  urlContextualisee: boolean;
  isSetupRegion: boolean;
  isUserDashboard: boolean;
}

@Component({
  selector: 'app-nav-bar-top',
  templateUrl: './nav-bar-top.component.html',
  styleUrls: ['./nav-bar-top.component.scss'],
  standalone: false
})
export class NavBarTopComponent {
  // Route configuration ordered by specificity (most specific first)
  private readonly ROUTE_CONFIGS: RouteConfig[] = [
    { pattern: 'site-setup', infoRoute: 'site-setup', urlContextualisee: true },
    { pattern: 'region-setup', infoRoute: 'region', isSetupRegion: true },
    { pattern: 'setup-organization', infoRoute: 'setup-organization', isUserDashboard: true },
    { pattern: 'mysite', infoRoute: 'mysite', urlContextualisee: true },
    { pattern: 'myblasts', infoRoute: 'myblasts', urlContextualisee: true },
    { pattern: 'myprojects', infoRoute: 'myprojects', urlContextualisee: true },
    { pattern: 'workflows-region', infoRoute: 'workflows-region' },
    { pattern: 'workflows', infoRoute: 'workflows', urlContextualisee: true },
    { pattern: 'config-analysis', infoRoute: 'config-analysis', urlContextualisee: true },
    { pattern: 'area-dashboard', infoRoute: 'area-dashboard', isSetupRegion: true },
    { pattern: 'user-dashboard', infoRoute: 'user-dashboard', isUserDashboard: true },
    { pattern: 'dashboard', infoRoute: 'dashboard', urlContextualisee: true },
    { pattern: 'indicators', infoRoute: 'indicators', urlContextualisee: true },
    { pattern: 'operators', infoRoute: 'operators', urlContextualisee: true },
    { pattern: 'regions', infoRoute: 'regions' },
    { pattern: 'users', infoRoute: 'users' },
    { pattern: 'sites', infoRoute: 'sites' }
  ];
  traductions = signal<string[]>([]);
  titre = signal<string>('');
  listeRegions = signal<Region[]>([]);
  listeRegions_filtred = signal<Region[]>([]);
  listeSites = signal<Site[]>([]);
  listeSites_filtred = signal<Site[]>([]);
  infoRoute = signal<string>('');
  isUserLogged = signal<boolean>(false);
  emailSupport = signal<string>('');
  isExpanded = signal<boolean>(false);
  modaleMdp = signal<BsModalRef>(null);
  previousRoute = signal<string>('');
  urlContextualisee = signal<boolean>(false);
  private readonly _query = signal<string>('');
  get query() { return this._query(); }
  set query(value: string) { this._query.set(value); }
  isSetupRegion = signal<boolean>(false);
  isUserDashboard = signal<boolean>(false);
  userProfile = signal<KeycloakProfile>(null);
  profileUsername = signal<string>(null);
  organizations = signal<OrganizationDto[]>([]);
  organizations_filtered = signal<OrganizationDto[]>([]);
  selectedOrganization = signal<OrganizationDto>(null);
  areaFeatureFlag = signal<FeatureFlag | undefined>(undefined);
  canAccessArea = signal<boolean>(false);

  config = {
    backdrop: true,
    ignoreBackdropClick: true,
    keyboard: false
  };

  private readonly keycloak = inject(Keycloak);

  constructor(
    private readonly http: HttpService,
    public router: Router,
    private readonly traductionService: TraductionService,
    private readonly modalService: BsModalService,
    public cookieService: CookieService,
    protected utilisateurService: UtilisateurService,
    private readonly keycloakUtilisateurService: KeycloakUtilisateurService,
    private readonly sharedOrganizationService: SharedOrganizationService,
    private readonly authService: AuthenticationService
    ){

      this.utilisateurService.listeSites$.subscribe((res)=>{
        this.listeSites.set(res);
        this.setSitesNamesWithRegions();
        this.refreshAreaAccess();
       })
    
    // router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((val: NavigationEnd) => {
    //   if (this.previousRoute == '/sites')
    //     this.refreshSite();
    //   this.previousRoute = val.url;
    // });
  }

  loadOrganizations() {
    this.keycloakUtilisateurService.getCurrentUserOrganizations()
      .then(organizations => {
        this.organizations.set(organizations);
        this.selectedOrganization.set(organizations.length == 1 ? organizations[0] : null);
        this.sharedOrganizationService.setSelectedOrganization(this.selectedOrganization());
      })
      .catch(error => {
        console.error('Error loading organizations:', error);
      });
  }

  selectOrganization(org: OrganizationDto) {
    this.selectedOrganization.set(org);
    this.sharedOrganizationService.setSelectedOrganization(org);
  }

  filterOrganizations(event: any) {
    var key = this._query().toLowerCase();
    const filtered = this.organizations().filter(org => {
      return org.name.toLowerCase().includes(key);
    });
    
    filtered.sort((a, b) => {
      if (a.name < b.name)
        return -1;
      if (a.name > b.name)
        return 1;
      return 0;
    });
    this.organizations_filtered.set(filtered);
  }

  onOpenChangeOrganization(isopen: boolean) {
    if (!isopen) {
      this._query.set("");
    }
    this.organizations_filtered.set(this.organizations());
  }

  /**
   * Detects the current route information based on URL segments
   * Returns route metadata including infoRoute identifier and context flags
   */
  private detectRouteInfo(): RouteInfo {
    const url = this.router.url;
    const segments = url.split('/').filter(s => s && !s.includes('?'));
    
    // Find matching route config by checking if any segment matches the pattern
    const matchedConfig = this.ROUTE_CONFIGS.find(config => 
      segments.some(segment => segment === config.pattern || segment.includes(config.pattern))
    );

    if (matchedConfig) {
      return {
        infoRoute: matchedConfig.infoRoute,
        urlContextualisee: matchedConfig.urlContextualisee ?? false,
        isSetupRegion: matchedConfig.isSetupRegion ?? false,
        isUserDashboard: matchedConfig.isUserDashboard ?? false
      };
    }

    // Default fallback
    return {
      infoRoute: '',
      urlContextualisee: false,
      isSetupRegion: false,
      isUserDashboard: false
    };
  }

  ngOnInit() {
    this.userProfile.set(this.keycloakUtilisateurService.userProfile);
    this.isUserLogged.set(this.userProfile() != null);
    this.listeSites.set(this.utilisateurService.listeSites);
    this.setSitesNamesWithRegions();
    this.loadAreaFeatureFlag();


    this.init();
    
    // Detect current route and set flags
    const routeInfo = this.detectRouteInfo();
    this.infoRoute.set(routeInfo.infoRoute);
    this.urlContextualisee.set(routeInfo.urlContextualisee);
    this.isSetupRegion.set(routeInfo.isSetupRegion);
    this.isUserDashboard.set(routeInfo.isUserDashboard);

    if (this.isSetupRegion()) {
      this.setRegions();
    }

    if (this.isUserDashboard()) {
      this.loadOrganizations();
    }

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        // Detect current route and update flags
        const routeInfo = this.detectRouteInfo();
        this.infoRoute.set(routeInfo.infoRoute);
        this.urlContextualisee.set(routeInfo.urlContextualisee);
        this.isSetupRegion.set(routeInfo.isSetupRegion);
        this.isUserDashboard.set(routeInfo.isUserDashboard);

        if (this.isSetupRegion()) {
          this.setRegions();
        }

        if (this.isUserDashboard()) {
          this.loadOrganizations();
        }
      }
    });

  }

  init() {
    //this.isUserLogged = this.utilService.utilisateur !== null && this.utilService.utilisateur !== undefined;
    // if (this.keycloakService.isLoggedIn()) {
    //   this.isUserLogged = true;
    // }
    if (this.utilisateurService.isLogin) {
      this.http.get<string>('Config/EmailAdministrateur').subscribe(resultat => {
        this.emailSupport.set(resultat);
      });
    }
  }

  setSitesNamesWithRegions(){
    const sites = this.listeSites();
    sites.forEach(p => p.groupByNomRegion = p.region.nom);
    sites.sort((a, b) => {
          if (a.groupByNomRegion < b.groupByNomRegion)
            return -1;
          if (a.groupByNomRegion == b.groupByNomRegion) {
            if (a.nom > b.nom)
              return 1;
            else
              return -1;
          }
          if (a.groupByNomRegion > b.groupByNomRegion)
            return 1;
        })
        this.listeSites.set(sites);
        this.listeSites_filtred.set(sites);
  }

  setRegions(){
    this.http.get('Regions').subscribe((regions: Region[]) => {
      //if(this.utilisateurService.userRole === ExploreRole.ADMIN){
        regions.sort((a, b) => a.nom.localeCompare(b.nom))
        this.listeRegions.set(regions);
        this.listeRegions_filtred.set(regions);
      // }
      // else if (this.utilisateurService.userRole === ExploreRole.RESP_REGION){
      //   let userRegionsId = this.utilisateurService.userRegions.map( region => region.regionId);
      //   this.listeRegions = regions.filter( reg => userRegionsId.includes(reg.regionId));
      // }
      this.definirRegions(this.listeRegions());
      if(this.utilisateurService.currentRegionForSetupRegion == undefined || this.utilisateurService.currentRegionForSetupRegion == null || Object.keys(this.utilisateurService.currentRegionForSetupRegion).length === 0){
       
        let regionIdFromCookie = this.utilisateurService.getCurrentRegionIdFromCookie();
       if(regionIdFromCookie != undefined){
         let region = this.listeRegions().find(r => r.regionId === regionIdFromCookie);
         if(region != undefined){
            this.definirRegion(region);
         }
         else if(this.listeRegions().length > 0){
            this.definirRegion(this.listeRegions()[0]);
         }
       }
       else if(this.utilisateurService.currentSite() != undefined){
         this.definirRegion(this.utilisateurService.currentSite().region);
       }
      }
     });
  }

  definirRegion(r: Region){
    if(r.nom === this.utilisateurService.currentRegionForSetupRegion.nom) return;

    this.utilisateurService.setCurrentRegionForProducts(r);
  }

  definirRegions(r: Region[]){
    this.utilisateurService.setRegionsForProducts(r);
  }

  definirSiteEnCours(site: Site) {
    this.utilisateurService.currentEtape = new WorkflowEtapeInstance();
    this.utilisateurService.setCurrentSite(site);
    this.definirRegion(site.region);
    //sessionStorage.removeItem(Constants.StockageConfigurationFiltrage);
    localStorage.removeItem(Constants.StockageConfigurationFiltrage);
    if (!this.router.url.includes('site-setup') && !this.router.url.includes('workflows') && !this.router.url.includes('config-analysis')) {
      if (this.router.url.includes("myblasts")) {
        this.router.navigate(['/mysite', site.siteId, 'myblasts'], { replaceUrl: true });
      } else if (this.router.url.includes("myprojects")) {
        this.router.navigate(['/mysite', site.siteId, 'myprojects'], { replaceUrl: true });
      } else if (this.router.url.includes("indicators")) {
        this.router.navigate(['/mysite', site.siteId, 'indicators'], { replaceUrl: true });
      } else if (this.router.url.includes("dashboard")) {
        this.router.navigate(['/mysite', site.siteId, 'dashboard'], { replaceUrl: true });
      } else {
        this.router.navigate(['/mysite', site.siteId], { replaceUrl: true });
      }
    }
    this.utilisateurService.getSites();
  }

  changerLangue(langueSelectionnee: string) {
    this.traductionService.changerLangue(langueSelectionnee, true);
  }

   logout() {
    this.authService.logout();
  }

  ouvrirFenetreMdp() {
    this.keycloakUtilisateurService.triggerPasswordReset();    
  }

  ouvrirFenetreLangue() {
    this.modaleMdp.set(this.modalService.show(ModaleChangerLangueComponent, this.config));
  }

  filter(event: any) {
    var key = this._query().toLowerCase();
    const filtered = this.listeSites().filter(site => {
      return site.nom.toLowerCase().includes(key) || site.region.nom.toLowerCase().includes(key);
    });
    filtered.forEach(p => p.groupByNomRegion = p.region.nom);
    filtered.sort((a, b) => {
      if (a.groupByNomRegion < b.groupByNomRegion)
        return -1;
      if (a.groupByNomRegion == b.groupByNomRegion) {
        if (a.nom > b.nom)
          return 1;
        else
          return -1;
      }
      if (a.groupByNomRegion > b.groupByNomRegion)
        return 1;
    })
    this.listeSites_filtred.set(filtered);
  }

  filterRegions(event: any) {
    var key = this._query().toLowerCase();
    const filtered = this.listeRegions().filter(region => {
      return region.nom.toLowerCase().includes(key) || region.nom.toLowerCase().includes(key);
    });
    // this.listeRegions_filtred.forEach(p => p.groupByNomRegion = p.region.nom);
    filtered.sort((a, b) => {
      if (a.nom < b.nom)
        return -1;
      if (a.nom == b.nom) {
        if (a.nom > b.nom)
          return 1;
        else
          return -1;
      }
      if (a.nom > b.nom)
        return 1;
    })
    this.listeRegions_filtred.set(filtered);
  }

  onOpenChange(isopen: boolean) {
    if (!isopen) {
      this._query.set("");
      }
    this.listeSites_filtred.set(this.listeSites());
  }

  onOpenChangeRegion(isopen: boolean){
    if (!isopen) {
      this._query.set("");
      }
    this.listeRegions_filtred.set(this.listeRegions());
  }

  showProfile() {
    this.router.navigate(['/profile'], { replaceUrl: true });
  }

  navigateToOurProducts() {
    this.router.navigate(['/products']);
  }

  private loadAreaFeatureFlag() {
    this.http.get<FeatureFlag[]>('FeatureFlag').subscribe({
      next: flags => {
        this.areaFeatureFlag.set(flags.find(f => f.featureName === AREA_FEATURE_NAME));
        this.refreshAreaAccess();
      },
      error: () => {
        this.areaFeatureFlag.set(undefined);
        this.refreshAreaAccess();
      },
    });
  }

  private refreshAreaAccess() {
    const orgIds = getOrganizationIdsFromSites(this.listeSites());
    this.canAccessArea.set(isAreaEnabledForUser(this.areaFeatureFlag(), orgIds));
  }

}
